import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Trash2, Pencil, Calendar, Flag, X, CheckSquare, Square, FolderPlus, Folder, Settings as SettingsIcon } from 'lucide-react';
import { format, parseISO, isToday, isPast } from 'date-fns';
import { de } from 'date-fns/locale';
import { useTranslation } from '../lib/i18n';
import { useConfirm, usePrompt } from '../lib/ConfirmDialog';

const PRIORITIES = [
  { id: 'low', labelKey: 'todo.priorityLow', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { id: 'medium', labelKey: 'todo.priorityMedium', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { id: 'high', labelKey: 'todo.priorityHigh', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' }
];

const VIEWS = [
  { id: 'today', labelKey: 'todo.viewToday' },
  { id: 'all', labelKey: 'todo.viewAll' },
  { id: 'completed', labelKey: 'todo.viewCompleted' }
];

const emptyDraft = () => ({
  id: null,
  title: '',
  date: '',
  priority: '',
  note: '',
  groupId: ''
});

const Todo = ({ data, updateData }) => {
  const { t, lang } = useTranslation();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const tasks = data.todo?.tasks || [];
  const groups = data.todo?.groups || [];
  const [view, setView] = useState('today');
  const [selectedGroupId, setSelectedGroupId] = useState(''); // '' = all groups
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [showManageGroups, setShowManageGroups] = useState(false);

  const saveTasks = (next) => {
    updateData({ todo: { ...(data.todo || {}), tasks: next, groups } });
  };
  const saveGroups = (next) => {
    updateData({ todo: { ...(data.todo || {}), tasks, groups: next } });
  };

  const createGroup = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const existing = groups.find(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (existing) return existing.id;
    const id = 'g-' + Date.now().toString();
    saveGroups([...groups, { id, name: trimmed }]);
    return id;
  };

  const renameGroup = (id, name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    saveGroups(groups.map((g) => (g.id === id ? { ...g, name: trimmed } : g)));
  };

  const deleteGroup = async (id) => {
    if (!(await confirm({ message: t('todo.deleteGroupConfirm'), danger: true }))) return;
    saveGroups(groups.filter((g) => g.id !== id));
    // Un-assign tasks from this group
    updateData({
      todo: {
        ...(data.todo || {}),
        tasks: tasks.map((t) => (t.groupId === id ? { ...t, groupId: null } : t)),
        groups: groups.filter((g) => g.id !== id)
      }
    });
    if (selectedGroupId === id) setSelectedGroupId('');
  };

  const promptNewGroup = async () => {
    const name = await prompt({
      title: t('todo.groupNamePrompt'),
      placeholder: t('todo.newGroup'),
      confirmText: t('common.add')
    });
    if (!name) return null;
    return createGroup(name);
  };

  const openNew = () => {
    setDraft({ ...emptyDraft(), groupId: selectedGroupId || '' });
    setShowForm(true);
  };

  const openEdit = (task) => {
    setDraft({
      id: task.id,
      title: task.title || '',
      date: task.date || '',
      priority: task.priority || '',
      note: task.note || '',
      groupId: task.groupId || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setDraft(emptyDraft());
  };

  const saveDraft = () => {
    if (!draft.title.trim()) return;
    const payload = {
      title: draft.title.trim(),
      date: draft.date || null,
      priority: draft.priority || null,
      note: draft.note.trim() || null,
      groupId: draft.groupId || null
    };

    if (draft.id) {
      saveTasks(
        tasks.map((t) =>
          t.id === draft.id ? { ...t, ...payload } : t
        )
      );
    } else {
      const newTask = {
        id: Date.now().toString(),
        done: false,
        createdAt: new Date().toISOString(),
        ...payload
      };
      saveTasks([newTask, ...tasks]);
    }
    closeForm();
  };

  const toggleDone = (id) => {
    saveTasks(
      tasks.map((t) =>
        t.id === id
          ? {
              ...t,
              done: !t.done,
              completedAt: !t.done ? new Date().toISOString() : null
            }
          : t
      )
    );
  };

  const deleteTask = async (id) => {
    if (!(await confirm({ message: t('todo.confirmDelete'), danger: true }))) return;
    saveTasks(tasks.filter((task) => task.id !== id));
  };

  // Auto-purge tasks completed more than 24h ago. Runs once on mount and
  // every minute thereafter so a task that turns 24h old while the page is
  // open also disappears.
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  useEffect(() => {
    const tick = () => {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const cur = tasksRef.current || [];
      const next = cur.filter((task) => {
        if (!task.done) return true;
        const ts = task.completedAt ? Date.parse(task.completedAt) : NaN;
        if (isNaN(ts)) return true; // missing timestamp -> keep
        return ts >= cutoff;
      });
      if (next.length !== cur.length) saveTasks(next);
    };
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const base = selectedGroupId
      ? tasks.filter((t) => t.groupId === selectedGroupId)
      : tasks;
    let list;
    switch (view) {
      case 'today':
        list = base.filter(
          (t) => !t.done && t.date && isToday(parseISO(t.date))
        );
        break;
      case 'completed':
        list = base.filter((t) => t.done);
        break;
      case 'all':
      default:
        list = base;
        break;
    }
    // Sort: not-done first, then by priority, then by date asc, then by created desc
    const prio = { high: 0, medium: 1, low: 2, '': 3, null: 3, undefined: 3 };
    return [...list].sort((a, b) => {
      if (!!a.done !== !!b.done) return a.done ? 1 : -1;
      const pa = prio[a.priority ?? ''];
      const pb = prio[b.priority ?? ''];
      if (pa !== pb) return pa - pb;
      if (a.date && b.date) return a.date.localeCompare(b.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
  }, [tasks, view, selectedGroupId]);

  const counts = useMemo(() => {
    const base = selectedGroupId
      ? tasks.filter((t) => t.groupId === selectedGroupId)
      : tasks;
    const today = base.filter(
      (t) => !t.done && t.date && isToday(parseISO(t.date))
    ).length;
    const all = base.length;
    const completed = base.filter((t) => t.done).length;
    return { today, all, completed };
  }, [tasks, selectedGroupId]);

  const groupById = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = g;
    return m;
  }, [groups]);

  const formatDate = (iso) => {
    try {
      return format(
        parseISO(iso),
        'd. MMM yyyy',
        lang === 'de' ? { locale: de } : undefined
      );
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('todo.title')}</h2>
          <p className="text-sm sm:text-base text-gray-500">{t('todo.subtitle')}</p>
        </div>
      </header>

      {/* View tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {VIEWS.map((v) => {
          const count = counts[v.id] || 0;
          const active = view === v.id;
          return (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                active
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(v.labelKey)}
              <span
                className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                  active ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Group chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setSelectedGroupId('')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            selectedGroupId === ''
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
          }`}
        >
          {t('todo.allGroups')}
        </button>
        {groups.map((g) => (
          <button
            key={g.id}
            onClick={() =>
              setSelectedGroupId((curr) => (curr === g.id ? '' : g.id))
            }
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              selectedGroupId === g.id
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Folder size={12} />
            {g.name}
          </button>
        ))}
        <button
          onClick={async () => {
            const id = await promptNewGroup();
            if (id) setSelectedGroupId(id);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600"
        >
          <FolderPlus size={12} />
          {t('todo.newGroup')}
        </button>
        {groups.length > 0 && (
          <button
            onClick={() => setShowManageGroups(true)}
            className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium text-gray-400 hover:text-gray-700"
            title={t('todo.manageGroups')}
          >
            <SettingsIcon size={14} />
          </button>
        )}
      </div>

      {/* Add button */}
      {!showForm && (
        <button
          onClick={openNew}
          className="w-full flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
        >
          <Plus size={18} />
          <span>{t('todo.newTaskPlaceholder')}</span>
        </button>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {draft.id ? t('todo.editTitle') : t('todo.newTitle')}
            </h3>
            <button onClick={closeForm} className="p-1 hover:bg-gray-100 rounded">
              <X size={18} />
            </button>
          </div>

          <input
            autoFocus
            type="text"
            placeholder={t('todo.newTaskPlaceholder')}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                saveDraft();
              }
            }}
            className="input"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('todo.dateOptional')}
              </label>
              <input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('todo.priorityOptional')}
              </label>
              <div className="flex gap-1">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        priority: draft.priority === p.id ? '' : p.id
                      })
                    }
                    className={`flex-1 px-2 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      draft.priority === p.id
                        ? p.color
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {t(p.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('todo.groupOptional')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setDraft({ ...draft, groupId: '' })}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                  !draft.groupId
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {t('todo.noGroup')}
              </button>
              {groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setDraft({ ...draft, groupId: g.id })}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                    draft.groupId === g.id
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Folder size={10} />
                  {g.name}
                </button>
              ))}
              <button
                type="button"
                onClick={async () => {
                  const id = await promptNewGroup();
                  if (id) setDraft((d) => ({ ...d, groupId: id }));
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-500 hover:text-primary-600 hover:border-primary-400"
              >
                <FolderPlus size={10} />
                {t('todo.newGroupShort')}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('todo.noteOptional')}
            </label>
            <textarea
              rows={3}
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              className="input resize-none"
              placeholder={t('todo.notePlaceholder')}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button onClick={closeForm} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              onClick={saveDraft}
              disabled={!draft.title.trim()}
              className="btn-primary disabled:opacity-50"
            >
              {draft.id ? t('common.save') : t('common.add')}
            </button>
          </div>
        </div>
      )}

      {/* Task list */}
      {filtered.length === 0 ? (
        <div className="card text-center py-12">
          <CheckSquare size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            {view === 'today' && t('todo.emptyToday')}
            {view === 'upcoming' && t('todo.emptyUpcoming')}
            {view === 'all' && t('todo.emptyAll')}
            {view === 'completed' && t('todo.emptyCompleted')}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((task) => {
            const prio = PRIORITIES.find((p) => p.id === task.priority);
            const overdue =
              !task.done &&
              task.date &&
              isPast(parseISO(task.date)) &&
              !isToday(parseISO(task.date));
            return (
              <li
                key={task.id}
                className={`group flex items-start gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow ${
                  task.done ? 'opacity-60' : ''
                }`}
              >
                <button
                  onClick={() => toggleDone(task.id)}
                  className="mt-0.5 text-gray-400 hover:text-primary-600"
                  title={task.done ? t('todo.markOpen') : t('todo.markDone')}
                >
                  {task.done ? (
                    <CheckSquare size={20} className="text-primary-600" />
                  ) : (
                    <Square size={20} />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <p
                      className={`text-sm font-medium flex-1 ${
                        task.done ? 'line-through text-gray-400' : 'text-gray-900'
                      }`}
                    >
                      {task.title}
                    </p>
                    {prio && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded border ${prio.color} flex items-center gap-1 flex-shrink-0`}
                      >
                        <Flag size={10} />
                        {t(prio.labelKey)}
                      </span>
                    )}
                  </div>

                  {(task.date || task.note || task.groupId) && (
                    <div className="mt-1 space-y-1">
                      {task.groupId && groupById[task.groupId] && !selectedGroupId && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Folder size={12} />
                          {groupById[task.groupId].name}
                        </div>
                      )}
                      {task.date && (
                        <div
                          className={`flex items-center gap-1 text-xs ${
                            overdue ? 'text-red-600 font-medium' : 'text-gray-500'
                          }`}
                        >
                          <Calendar size={12} />
                          {formatDate(task.date)}
                          {overdue && <span>· {t('todo.overdue')}</span>}
                        </div>
                      )}
                      {task.note && (
                        <p className="text-xs text-gray-500 whitespace-pre-wrap">
                          {task.note}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(task)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                    title={t('common.edit')}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => deleteTask(task.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title={t('common.delete')}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Manage Groups Modal */}
      {showManageGroups && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowManageGroups(false)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{t('todo.manageGroups')}</h3>
              <button
                onClick={() => setShowManageGroups(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-2">
              {groups.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  {t('todo.noGroups')}
                </p>
              ) : (
                groups.map((g) => {
                  const taskCount = tasks.filter((t) => t.groupId === g.id).length;
                  return (
                    <div
                      key={g.id}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                    >
                      <Folder size={14} className="text-gray-500" />
                      <input
                        type="text"
                        defaultValue={g.name}
                        onBlur={(e) => {
                          if (e.target.value.trim() && e.target.value !== g.name) {
                            renameGroup(g.id, e.target.value);
                          }
                        }}
                        className="flex-1 bg-transparent text-sm font-medium text-gray-800 outline-none focus:bg-white focus:border focus:border-primary-500 focus:px-2 focus:py-1 focus:rounded"
                      />
                      <span className="text-xs text-gray-400">
                        {taskCount} {taskCount === 1 ? t('todo.tasksOne') : t('todo.tasksOther')}
                      </span>
                      <button
                        onClick={() => deleteGroup(g.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              )}
              <button
                onClick={() => promptNewGroup()}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-primary-400 hover:text-primary-600 text-sm"
              >
                <FolderPlus size={14} />
                {t('todo.newGroup')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Todo;
